import React, { useState, useRef } from 'react';
import { config, features } from '../config';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect, disabled }) => {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): boolean => {
    
    // Check file type
    if (!config.supportedFileTypes.includes(file.type)) {
      alert(`Unsupported file type. Please upload: ${config.supportedFileTypes.join(', ')}`);
      return false;
    }

    // Check file size
    if (file.size > config.maxFileSize) {
      const maxSizeMB = (config.maxFileSize / 1024 / 1024).toFixed(1);
      alert(`File too large. Maximum size: ${maxSizeMB}MB`);
      return false;
    }

    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && validateFile(file)) {
      onFileSelect(file);
      // Clear the input value to allow re-selection of the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    
    const file = event.dataTransfer.files[0];
    if (file && validateFile(file)) {
      onFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (features.enableDragDrop) {
      setDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  return (
    <div
      className={`file-upload ${dragOver ? 'drag-over' : ''} ${disabled ? 'disabled' : ''}`}
      onDrop={features.enableDragDrop ? handleDrop : undefined}
      onDragOver={features.enableDragDrop ? handleDragOver : undefined}
      onDragLeave={features.enableDragDrop ? handleDragLeave : undefined}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={config.supportedFileTypes.join(',')}
        onChange={handleFileChange}
        disabled={disabled}
        id="file-input"
      />
      <label htmlFor="file-input">
        <div className="upload-content">
          <div className="upload-icon">📄</div>
          <div className="upload-text">
            <strong>Choose a PDF file</strong>
            {features.enableDragDrop && ' or drag it here'}
          </div>
          <div className="upload-subtext">
            PDF files only • Max size: {(config.maxFileSize / 1024 / 1024).toFixed(0)}MB
          </div>
        </div>
      </label>
    </div>
  );
};
